import "./ticketsKanban.scss";
import {
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaFileExcel, FaInfoCircle, FaPlus } from "react-icons/fa";
import { MdViewColumn, MdViewList, MdOutlineMap } from "react-icons/md";
import { toast } from "react-toastify";
import { useGetTickets } from "hooks/useGetTickets";
import { Ticket } from "interfaces/entities/ticket.model";
import { TicketsFilterBar } from "./components/TicketsFilterBar";
import { TicketsKanbanView } from "./TicketsKanbanView";
import { TicketsTableView } from "./TicketsTableView";
import { useTicketsQueryParams } from "hooks/useTicketsQueryParams";
import ticketsService from "services/admin/ticketsService";

const KANBAN_LIMIT = 500;

const TicketsKanban = () => {
  const navigate = useNavigate();
  const { value, setMany, reset } = useTicketsQueryParams();

  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {};
    if (value.branchID) params.branchID = value.branchID;
    if (value.trainNumber) params.trainNumber = value.trainNumber;
    if (value.categoryID) params.categoryID = value.categoryID;
    if (value.type) params.type = value.type;
    if (value.period && value.period !== "all" && value.period !== "custom") {
      params.period = value.period;
    }
    if (value.period === "custom") {
      if (value.from) params.from = value.from;
      if (value.to) params.to = value.to;
    }
    if (value.view === "table") {
      params.page = value.page;
      params.limit = value.limit;
    } else {
      params.limit = KANBAN_LIMIT;
    }
    return params;
  }, [value]);

  const { data: ticketsResponse } = useGetTickets(queryParams);
  const tickets: Ticket[] = ticketsResponse?.rows || [];
  const total: number = ticketsResponse?.count || 0;

  // Просмотр/редактирование тикета — отдельная страница /admin/tickets/:id (раньше был Dialog).
  const handleTicketClick = (t: Ticket) => {
    navigate(`/admin/tickets/${t.id}`);
  };

  // Параметры экспорта повторяют активные фильтры списка, кроме categoryID
  // (иначе отчёт схлопнется в одну строку — категория и так является осью отчёта)
  // и пагинации, не относящейся к сводным цифрам.
  const [isExporting, setIsExporting] = useState(false);
  const handleExportXlsx = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const params: Record<string, string | number> = {};
      if (value.branchID) params.branchID = value.branchID;
      if (value.trainNumber) params.trainNumber = value.trainNumber;
      if (value.type) params.type = value.type;
      if (value.period && value.period !== "all" && value.period !== "custom") {
        params.period = value.period;
      }
      if (value.period === "custom") {
        if (value.from) params.from = value.from;
        if (value.to) params.to = value.to;
      }

      const response = await ticketsService.exportXlsx(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.download = `tickets-${today}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e?.message || "Не удалось экспортировать обращения");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="leads-kanban">
      <div className="flex-row flex-space-between items-center">
        <div className="flex gap-4 items-center">
          <h3 className="mb-4">
            Обращения
            <Tooltip title="Обращения - Это обращения клиентов к менеджерам. Они могут быть новыми, в работе, ожидающими ответа или закрытыми.">
              <IconButton>
                <FaInfoCircle size={14} />
              </IconButton>
            </Tooltip>
          </h3>
        </div>
        <div className="flex gap-2 items-center mb-4">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={value.view}
            onChange={(_, next) => next && setMany({ view: next, page: 1 })}
          >
            <ToggleButton value="kanban">
              <MdViewColumn size={16} className="mr-1" /> Доска
            </ToggleButton>
            <ToggleButton value="table">
              <MdViewList size={16} className="mr-1" /> Таблица
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => navigate("/admin/tickets/map")}
          >
            <MdOutlineMap size={16} className="mr-2" />
            На карте
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={handleExportXlsx}
            disabled={isExporting}
          >
            <FaFileExcel size={14} className="mr-2" />
            {isExporting ? "Экспорт..." : "Экспорт XLSX"}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => navigate("/admin/tickets/new")}
          >
            <FaPlus size={12} className="mr-2" />
            Создать обращение
          </Button>
        </div>
      </div>

      <TicketsFilterBar value={value} onChange={setMany} onReset={reset} />

      {value.view === "kanban" ? (
        <TicketsKanbanView tickets={tickets} onTicketClick={handleTicketClick} />
      ) : (
        <TicketsTableView
          tickets={tickets}
          total={total}
          page={value.page}
          limit={value.limit}
          onTicketClick={handleTicketClick}
          onPageChange={(page) => setMany({ page })}
        />
      )}
    </div>
  );
};
export default TicketsKanban;
